import React, { useState } from 'react';
import { X, ExternalLink, Zap, AlertCircle, Calendar, Activity, Loader2, CheckCircle2, Clock } from 'lucide-react';
import { Subscription, MonthlyUsage } from '../types';
import { supabase } from '../services/supabaseClient';

interface SubscriptionManagerModalProps {
    isOpen: boolean;
    onClose: () => void;
    subscription: Subscription | null;
    monthlyUsage: MonthlyUsage | null;
    onReactivatePlan: () => void;
}

export const SubscriptionManagerModal: React.FC<SubscriptionManagerModalProps> = ({
    isOpen,
    onClose,
    subscription,
    monthlyUsage,
    onReactivatePlan
}) => {
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    // Use plan_id ('pro' or 'free') as the source of truth
    const isPro = subscription?.plan_id === 'pro';
    const status = subscription?.status;

    // Canceled but still in active period (cancel_at_period_end = true, status = 'active')
    const isCanceledAtPeriodEnd = !!(subscription?.cancel_at_period_end && status === 'active');

    // Payment is overdue — should have been downgraded to free already, but show warning
    const isPastDue = status === 'past_due' || status === 'unpaid';

    // Fully canceled (period ended)
    const isFullyCanceled = status === 'canceled';

    const planName = subscription?.plan?.name || 'Plano Free';

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const currentPeriodEnd = formatDate(subscription?.current_period_end);

    const handleManageSubscription = async () => {
        setLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Usuário não autenticado.");

            const { data, error } = await supabase.functions.invoke('create-portal-session', {
                body: {},
                headers: {
                    Authorization: `Bearer ${session.access_token}`
                }
            });

            if (error) {
                console.error("Supabase function error:", error);
                throw new Error(error.message || "Erro na função.");
            }

            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error("Não foi possível gerar a URL do portal.");
            }
        } catch (err: any) {
            console.error(err);
            alert("Erro ao acessar o portal: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Determine status badge
    const renderStatusBadge = () => {
        if (isPastDue) {
            return <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-bold">Pagamento Pendente</span>;
        }
        if (isCanceledAtPeriodEnd) {
            return <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full font-bold">Cancelado</span>;
        }
        if (isFullyCanceled || !isPro) {
            return null; // Free plan, no special badge
        }
        return <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-bold">Ativo</span>;
    };

    // Determine billing/expiry info line
    const renderDateInfo = () => {
        if (!currentPeriodEnd) return null;

        if (isCanceledAtPeriodEnd) {
            return (
                <div className="flex items-center gap-2 text-sm text-yellow-600 mt-4 pt-4 border-t border-slate-200 bg-yellow-50 -mx-2 px-3 py-2 rounded-lg">
                    <Clock size={16} className="text-yellow-500 shrink-0" />
                    <span>
                        Acesso PRO válido até <strong>{currentPeriodEnd}</strong>. Após essa data, você será movido para o plano Free.
                    </span>
                </div>
            );
        }

        if (isPastDue) {
            return (
                <div className="flex items-center gap-2 text-sm text-red-600 mt-4 pt-4 border-t border-slate-200 bg-red-50 -mx-2 px-3 py-2 rounded-lg">
                    <AlertCircle size={16} className="text-red-500 shrink-0" />
                    <span>
                        Pagamento em atraso. Regularize seu pagamento para recuperar o acesso PRO.
                    </span>
                </div>
            );
        }

        if (isPro) {
            return (
                <div className="flex items-center gap-2 text-sm text-slate-500 mt-4 pt-4 border-t border-slate-200">
                    <Calendar size={16} className="text-blue-500 shrink-0" />
                    <span>Próxima cobrança: <strong>{currentPeriodEnd}</strong></span>
                </div>
            );
        }

        return null;
    };

    // Decide which action button to show
    const showManageButton = isPro && !isCanceledAtPeriodEnd && !isPastDue && !isFullyCanceled;
    const showReactivateButton = !isPro || isCanceledAtPeriodEnd || isPastDue || isFullyCanceled;

    const reactivateLabel = isPastDue
        ? "Regularizar Pagamento"
        : isCanceledAtPeriodEnd
            ? "Reativar Assinatura"
            : "Assinar o Plano PRO";

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-[32px] w-full max-w-lg shadow-2xl relative overflow-hidden">

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors z-10"
                    >
                        <X size={20} />
                    </button>
                    <h2 className="text-2xl font-bold mb-2">Sua Assinatura</h2>
                    <p className="text-blue-100 opacity-90">Gerencie seu plano e consumo mensal</p>
                </div>

                <div className="p-8 space-y-8">

                    {/* Status Panel */}
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">PLANO ATUAL</h3>
                                <div className="text-xl font-bold text-slate-800 flex items-center gap-2 flex-wrap">
                                    {planName}
                                    {renderStatusBadge()}
                                </div>
                            </div>
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm shrink-0 ${isPro ? 'bg-gradient-to-br from-yellow-400 to-orange-400 text-white' : 'bg-slate-200 text-slate-400'}`}>
                                <Zap size={24} className={isPro ? "fill-white" : ""} />
                            </div>
                        </div>

                        {renderDateInfo()}
                    </div>

                    {/* Usage Panel */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                            <Activity size={18} className="text-blue-500" />
                            Consumo Mensal
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-slate-500 font-medium">Análises de Vídeo</span>
                                    <span className="text-slate-800 font-bold">
                                        {monthlyUsage?.projects_count || 0} / {subscription?.plan?.max_projects_per_month === -1 ? 'Ilimitado' : subscription?.plan?.max_projects_per_month || 3}
                                    </span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-2.5">
                                    <div
                                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                        style={{
                                            width: subscription?.plan?.max_projects_per_month === -1
                                                ? '100%'
                                                : `${Math.min(100, ((monthlyUsage?.projects_count || 0) / (subscription?.plan?.max_projects_per_month || 3)) * 100)}%`
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-4 border-t border-slate-100">
                        {showReactivateButton ? (
                            <button
                                onClick={onReactivatePlan}
                                className="w-full bg-slate-900 text-white font-bold py-4 rounded-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-lg shadow-slate-900/20"
                            >
                                <Zap size={20} className="fill-white" />
                                {reactivateLabel}
                            </button>
                        ) : null}

                        {showManageButton ? (
                            <button
                                onClick={handleManageSubscription}
                                disabled={loading}
                                className="w-full bg-white border-2 border-slate-200 text-slate-700 font-bold py-4 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {loading ? (
                                    <Loader2 className="animate-spin" size={20} />
                                ) : (
                                    <>
                                        <ExternalLink size={20} />
                                        Gerenciar Assinatura
                                    </>
                                )}
                            </button>
                        ) : null}

                        {/* Manage button also available when canceled-at-period-end (to allow reactivation via portal) */}
                        {isCanceledAtPeriodEnd && (
                            <button
                                onClick={handleManageSubscription}
                                disabled={loading}
                                className="w-full mt-3 bg-white border-2 border-slate-200 text-slate-500 font-medium py-3 rounded-xl hover:bg-slate-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                            >
                                {loading ? (
                                    <Loader2 className="animate-spin" size={16} />
                                ) : (
                                    <>
                                        <ExternalLink size={16} />
                                        Gerenciar via portal Stripe
                                    </>
                                )}
                            </button>
                        )}

                        {showManageButton && (
                            <p className="text-center text-xs text-slate-400 mt-4 leading-relaxed">
                                Você será redirecionado para o portal seguro da Stripe para alterar dados de pagamento ou cancelar a sua assinatura.
                            </p>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};
